import { useTranslation } from 'react-i18next'

import { Button, Flex, SearchInput } from '@percent/lemonade'
import { ErrorView } from '@percent/partner-dashboard/common/components'
import { usePermissionCheck } from '@percent/partner-dashboard/common/hooks/useAllow/usePermissionCheck'
import { GivingAction } from '@percent/partner-dashboard/services/givingActions/givingActionsService.types'
import {
  GivingActionsDialogsProvider,
  useGivingActionsDialogs
} from './GivingActionsDialogs/GivingActionsDialogsProvider'
import { GivingActionsTable } from './GivingActionsTable/GivingActionsTable'
import { useGivingActionsTable } from './useGivingActionsTable'
import styles from './ActionsTab.module.scss'

export function ActionsTab() {
  const {
    data,
    isLoading,
    errorMessage,
    totalResults,
    nextPage,
    previousPage,
    refresh,
    searchValue,
    searchQuery,
    handleSearchChange,
    handleSearchKeyDown,
    handleSearchClear
  } = useGivingActionsTable()

  return (
    <GivingActionsDialogsProvider refresh={refresh}>
      <ActionsTabContent
        data={data ?? []}
        isLoading={isLoading}
        errorMessage={errorMessage}
        totalResults={totalResults}
        nextPage={nextPage}
        previousPage={previousPage}
        searchValue={searchValue}
        searchQuery={searchQuery}
        handleSearchChange={handleSearchChange}
        handleSearchKeyDown={handleSearchKeyDown}
        handleSearchClear={handleSearchClear}
      />
    </GivingActionsDialogsProvider>
  )
}

type ActionsTabContentProps = Readonly<{
  data: GivingAction[]
  isLoading: boolean
  errorMessage: string
  totalResults: number | null
  nextPage: (() => void) | null
  previousPage: (() => void) | null
  searchValue: string
  searchQuery: string
  handleSearchChange: React.ChangeEventHandler<HTMLInputElement>
  handleSearchKeyDown: React.KeyboardEventHandler<HTMLInputElement>
  handleSearchClear: () => void
}>

function ActionsTabContent({
  data,
  isLoading,
  errorMessage,
  totalResults,
  nextPage,
  previousPage,
  searchValue,
  searchQuery,
  handleSearchChange,
  handleSearchKeyDown,
  handleSearchClear
}: ActionsTabContentProps) {
  const { t } = useTranslation()
  const { userCan } = usePermissionCheck()
  const { createGivingAction } = useGivingActionsDialogs()

  const canManage = userCan('manageGivingActions')

  if (errorMessage) {
    return <ErrorView errorMessage={errorMessage} />
  }

  return (
    <Flex direction="column">
      <div className={styles.filterContainer}>
        <SearchInput
          name="search"
          value={searchValue}
          onKeyDown={handleSearchKeyDown}
          onChange={handleSearchChange}
          handleClearValue={handleSearchClear}
          placeholder={t('typography.givingActions.searchPlaceholder')}
        />
        {canManage && (
          <Button onClick={createGivingAction} data-testid="giving-action-create-button">
            {t('button.createGivingAction')}
          </Button>
        )}
      </div>
      <GivingActionsTable
        data={data}
        isLoading={isLoading}
        canManage={canManage}
        totalResults={totalResults}
        searchQuery={searchQuery}
        nextPage={nextPage}
        previousPage={previousPage}
        onCreate={createGivingAction}
      />
    </Flex>
  )
}
